import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { bookingErrorText } from "../lib/booking-error";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bookingsApi, bookingKeys, type BookingDetail } from "../api/bookings.api";
import { toLocalISO } from "../lib/local-date";
import { CalendarGrid } from "./booking-calendar";
import { PW } from "../internal-ui/pewter-shell";
import { PewterTimeField } from "../internal-ui/pewter-pickers";
import { BookingSkinScope } from "../skin";
import {
  pewterCloseButton,
  pewterControlSoft,
  pewterDialog,
  pewterErrorNote,
  pewterInput,
  pewterPrimaryButton,
  pewterScrim,
} from "../internal-ui/pewter-modal";

import { Icon } from "@majourneys/icons";
import { moroccoStartOfToday } from "@majourneys/shared";
interface ModifyBookingModalProps {
  booking: BookingDetail;
  onClose: () => void;
  onModified?: () => void;
}

function todayIso(): string {
  // Morocco's calendar date, not the host's and not UTC. The booking rules
  // judge "already past" against the Morocco wall clock, so a guest whose own
  // midnight has turned must not be offered, or refused, a different day here.
  return toLocalISO(moroccoStartOfToday());
}

export function ModifyBookingModal({ booking, onClose, onModified }: ModifyBookingModalProps) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const timeLabelId = useId();

  const isStay = booking.pricing.typePrice === 2;
  const editGuests = booking.pricing.typePrice === 0;

  const [date, setDate] = useState(booking.reservation.date || todayIso());
  const [time, setTime] = useState(booking.reservation.time ?? "");
  const [adults, setAdults] = useState(booking.reservation.adults);
  const [children, setChildren] = useState(booking.reservation.children);
  const [infants, setInfants] = useState(booking.reservation.infants);

  const dirty = useMemo(() => {
    if (date !== booking.reservation.date) return true;
    if ((time || "") !== (booking.reservation.time ?? "")) return true;
    if (editGuests) {
      if (adults !== booking.reservation.adults) return true;
      if (children !== booking.reservation.children) return true;
      if (infants !== booking.reservation.infants) return true;
    }
    return false;
  }, [
    date,
    time,
    adults,
    children,
    infants,
    editGuests,
    booking.reservation.date,
    booking.reservation.time,
    booking.reservation.adults,
    booking.reservation.children,
    booking.reservation.infants,
  ]);

  const guestsValid = !editGuests || adults + children + infants >= 1;

  const mutation = useMutation({
    mutationFn: () =>
      bookingsApi.modify(booking.reference, {
        date: date !== booking.reservation.date ? date : undefined,
        time: (time || "") !== (booking.reservation.time ?? "") ? time : undefined,
        adults: editGuests && adults !== booking.reservation.adults ? adults : undefined,
        children: editGuests && children !== booking.reservation.children ? children : undefined,
        infants: editGuests && infants !== booking.reservation.infants ? infants : undefined,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: bookingKeys.detail(booking.reference, i18n.language) });
      onModified?.();
      onClose();
    },
  });

  // Mapped by the API's code to copy in the guest's language: never the
  // API's own English, nor a 422's validation JSON or "Failed to fetch".
  const errorText = mutation.error
    ? bookingErrorText(mutation.error, t as never, "modify")
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("activity.modify_booking", { defaultValue: "Modify booking" })}
      className={`fixed inset-0 z-[70] flex items-end justify-center p-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:items-center sm:p-4 ${pewterScrim}`}
      onClick={onClose}
    >
      {/* Pewter, like the sheet it opens from (owner, 2026-09-10). The navy
          header block is gone: the frame is the surface now, and a solid navy
          bar across the top of it read as a second, competing chrome. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[calc(86dvh_/_var(--mj-ui-zoom,1))] w-full overflow-y-auto rounded-[26px] p-4 pb-5 sm:max-w-lg"
        style={pewterDialog()}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-[17px] font-bold text-[#110E39]">
              {t("activity.modify_booking", { defaultValue: "Modify booking" })}
            </h2>
            <p className="mt-0.5 break-words font-sans text-[12px] text-[#5a5d77]">
              {booking.activity.title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            style={pewterCloseButton}
          >
            <Icon name="ri-close-line" className="text-[16px] text-[#110E39]" aria-hidden />
          </button>
        </div>

        <p className="mt-4 rounded-2xl px-3 py-2.5 font-sans text-[12px] leading-relaxed text-[#5a5d77]"
           style={{ background: PW.softTint }}>
          <Icon name="ri-information-line" className="me-1 align-middle text-[#8A6425]" aria-hidden />
          {t("activity.modify_pending_only_hint", {
            defaultValue:
              "Changes are only possible while we haven't confirmed yet. Our team will re-confirm with the provider after you save.",
          })}
        </p>

        {/* The funnel's own calendar, not <input type="date"> (owner,
            2026-09-10: "calendar must be in pewter"). The native control opens
            the browser's picker, which follows the OS colour scheme — on a dark
            one it dropped a black panel over a pewter dialog, and no amount of
            CSS on our side can reach inside it. CalendarGrid is the same grid
            the booking sheet uses, so the two agree, and it already refuses
            past days. */}
        <div className="mt-4">
          <span className="font-sans text-[11px] font-bold uppercase tracking-wider text-[#5a5d77]">
            {isStay
              ? t("activity.check_in", { defaultValue: "Check-in" })
              : t("activity.date", { defaultValue: "Date" })}
          </span>
          <div className="mt-1.5 rounded-2xl p-2" style={pewterInput}>
            <CalendarGrid selected={date || null} onSelect={setDate} />
          </div>
        </div>

        {/* The pewter time field, not <input type="time">: the native one
            opens the browser's own picker (owner rule: none on the site). The
            stored time is free text, so a value off the 15-minute grid still
            shows as it is and is only replaced when the guest picks another. */}
        {!isStay && (
          <div className="mt-3">
            <span
              id={timeLabelId}
              className="font-sans text-[11px] font-bold uppercase tracking-wider text-[#5a5d77]"
            >
              {t("activity.time", { defaultValue: "Time" })}
            </span>
            <div className="mt-1.5">
              <BookingSkinScope skin="pewter">
                <PewterTimeField
                  labelId={timeLabelId}
                  title={t("activity.time", { defaultValue: "Time" })}
                  value={time}
                  onChange={setTime}
                  placeholder={t("transport.choose_time", { defaultValue: "Choose a time" })}
                />
              </BookingSkinScope>
            </div>
          </div>
        )}

        {editGuests && (
          <div className="mt-3 grid grid-cols-3 gap-2.5">
            <NumField
              label={t("activity.adults", { defaultValue: "Adults" })}
              value={adults}
              onChange={setAdults}
              min={0}
            />
            <NumField
              label={t("activity.children", { defaultValue: "Children" })}
              value={children}
              onChange={setChildren}
              min={0}
            />
            <NumField
              label={t("activity.infants", { defaultValue: "Infants" })}
              value={infants}
              onChange={setInfants}
              min={0}
            />
          </div>
        )}

        {!guestsValid && (
          <p className="mt-3 font-sans text-[12px] font-semibold" style={{ color: PW.red }}>
            {t("activity.modify_min_one_guest", { defaultValue: "Add at least one guest." })}
          </p>
        )}

        {errorText && (
          <p
            role="alert"
            className="mt-3 rounded-xl px-3 py-2 font-sans text-[12.5px] font-semibold"
            style={pewterErrorNote}
          >
            {errorText}
          </p>
        )}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-full py-2.5 font-sans text-[12.5px] font-bold text-[#110E39]"
            style={pewterControlSoft}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            disabled={!dirty || !guestsValid || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 font-sans text-[12.5px] font-bold disabled:pointer-events-none disabled:opacity-50"
            style={pewterPrimaryButton}
          >
            <Icon
              name={mutation.isPending ? "ri-loader-4-line animate-spin" : "ri-check-line"}
              className="text-[14px]"
              aria-hidden
            />
            {t("activity.save_changes", { defaultValue: "Save changes" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  min,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
}) {
  return (
    <label className="block">
      <span className="font-sans text-[11.5px] font-bold uppercase tracking-wider text-[#5a5d77]">{label}</span>
      <div className="mt-1.5 flex items-center justify-between rounded-2xl px-1.5 py-1" style={pewterInput}>
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#110E39] hover:bg-[#f6f3ff]"
          aria-label="-"
        >
          <Icon name="ri-subtract-line" aria-hidden />
        </button>
        <span className="font-heading text-[15px] font-bold text-[#110E39]">{value}</span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#110E39] hover:bg-[#f6f3ff]"
          aria-label="+"
        >
          <Icon name="ri-add-line" aria-hidden />
        </button>
      </div>
    </label>
  );
}
